/**
 * Hiring-demand board — the shared visual vocabulary.
 *
 * The one rule that matters here: an HDB row and a draft must never be mistaken
 * for one another. HDB rows are solid and plain; drafts are dashed, italic and
 * blue-railed. That is a data-integrity concern dressed as styling — a lead who
 * reads provisional demand as approved headcount plans against a number that
 * does not exist.
 *
 * The traffic lights (green = closed, blue = live, amber = held) sit outside the
 * brand book's monochrome-plus-Trusted-Blue rule for the same reason the
 * Check-In tab's do: a lead scanning twelve months reads colour faster than text.
 */
import type { BoardRole, DemandDraft, HdbStatus, HireReason } from '../../../types/taDemand';
import { isDraft } from '../../../types/taDemand';
import type { CellTally, Track } from './demandModel';
import { trackBand } from './demandModel';

export const STATUS_LABEL: Record<HdbStatus, string> = {
  hired: 'hired',
  open: 'open',
  hold: 'on hold',
  backlog: 'backlog',
};

const STATUS_CHIP: Record<HdbStatus, string> = {
  hired: 'bg-emerald-50 text-emerald-700',
  open: 'bg-blue-50 text-blue-700',
  hold: 'bg-amber-50 text-amber-700',
  backlog: 'bg-gray-100 text-gray-600',
};

const DRAFT_CHIP: Record<DemandDraft['state'], string> = {
  draft: 'border border-dashed border-blue-500 text-blue-700',
  approving: 'bg-blue-50 text-blue-700',
  synced: 'bg-emerald-50 text-emerald-700',
};

const DRAFT_LABEL: Record<DemandDraft['state'], string> = {
  draft: 'draft',
  approving: 'writing to HDB…',
  synced: 'in HDB',
};

const STATUS_HELP: Record<HdbStatus, string> = {
  hired: 'Somebody has started, or their start date is confirmed.',
  open: 'Being recruited for now. Counts as upcoming work.',
  hold: 'Approved but paused. It gets a NEW start date when someone decides to recruit for it again, so this date is provisional — not counted as upcoming work, and never marked overdue.',
  backlog: 'Approved and dated, not opened yet because the start is far enough out. Counts as upcoming work.',
};

export function RoleStateChip({ role }: { role: BoardRole }) {
  const cls = isDraft(role) ? DRAFT_CHIP[role.state] : STATUS_CHIP[role.status];
  const label = isDraft(role) ? DRAFT_LABEL[role.state] : STATUS_LABEL[role.status];
  const help = isDraft(role)
    ? 'Entered here, not yet in the Hiring Database.'
    : STATUS_HELP[role.status];
  return (
    <span
      title={help}
      className={`shrink-0 cursor-help rounded-sm px-1.5 text-[11px] font-semibold leading-4 ${cls}`}
    >
      {label}
    </span>
  );
}

/**
 * The marker in a month cell. Shows the headcount when a row covers more than
 * one hire, so two starts on the same date stay one row.
 */
export function RoleMarker({ role }: { role: BoardRole }) {
  const multi = role.count > 1;
  const shape = 'grid h-5 min-w-5 shrink-0 place-items-center rounded-full px-1 text-[10px] font-bold tabular-nums';
  if (isDraft(role)) {
    const cls =
      role.state === 'draft'
        ? 'border-2 border-dashed border-blue-500 bg-white text-blue-700'
        : 'border-2 border-blue-500 bg-blue-50 text-blue-700';
    return <span className={`${shape} ${cls}`}>{multi ? role.count : '·'}</span>;
  }
  if (role.status === 'hired') {
    return (
      <span className={`${shape} bg-emerald-500 text-white`}>{multi ? role.count : '✓'}</span>
    );
  }
  if (role.status === 'open') {
    return <span className={`${shape} bg-blue-600 text-white`}>{multi ? role.count : '·'}</span>;
  }
  if (role.status === 'hold') {
    return (
      <span className={`${shape} border-2 border-amber-500 text-amber-700`}>
        {multi ? role.count : '·'}
      </span>
    );
  }
  return (
    <span className={`${shape} border-2 border-dashed border-gray-300 text-gray-500`}>
      {multi ? role.count : '·'}
    </span>
  );
}

/**
 * Why the role exists. Backfill is called out because it behaves differently in
 * planning: it is triggered by a leaver and its clock starts on their notice.
 */
export function ReasonChip({
  reason,
  backfillFor,
}: {
  reason: HireReason;
  /** Whose seat this replaces. Shown on HOVER, not beside the chip — it was the
      widest column on the board and it pushed the role titles into an ellipsis. */
  backfillFor?: string;
}) {
  if (reason === 'unknown') return null;
  const backfill = reason === 'backfill';
  return (
    <span
      className={`shrink-0 rounded-sm px-1.5 text-[11px] leading-4 ${
        backfill ? 'bg-violet-50 text-violet-700' : 'bg-gray-100 text-gray-500'
      }`}
      title={
        backfill
          ? backfillFor
            ? `Replaces ${backfillFor}`
            : 'Replaces a leaver'
          : 'Additional headcount'
      }
    >
      {backfill ? 'backfill' : 'new hire'}
      {backfill && backfillFor && <span aria-hidden> ·</span>}
    </span>
  );
}

/**
 * The running shortfall on a group row.
 *
 * Always occupies its line, even at zero. Rendering nothing let the count above
 * it re-centre in the cell, so a month with a delta and a month without sat at
 * different heights and the row read as ragged.
 */
export function DeltaBadge({ delta }: { delta: number }) {
  return (
    <span
      aria-hidden={!delta}
      className={`h-3 text-[10px] font-semibold leading-3 tabular-nums ${
        delta ? 'text-rose-600' : 'text-transparent'
      }`}
      title={
        delta
          ? `${Math.abs(delta)} planned hire${Math.abs(delta) === 1 ? '' : 's'} still not started`
          : undefined
      }
    >
      {delta || '\u00a0'}
    </span>
  );
}

/**
 * A rolled-up count on a department / unit / team row.
 *
 * Colour carries the state of the month, not just its size: green when
 * everything in it is hired, blue when the unit has added demand beyond the HDB,
 * plain when it matches. That is what makes a collapsed row still worth reading.
 */
export function TallyPill({ cell }: { cell: CellTally }) {
  if (cell.total === 0) {
    // Same box as a real pill, so an empty month does not shift its neighbours.
    return <span className="grid h-[22px] place-items-center text-xs text-gray-300">·</span>;
  }
  const cls =
    cell.drafts > 0
      ? 'bg-blue-50 text-blue-700'
      : cell.hired === cell.total
        ? 'bg-emerald-50 text-emerald-700'
        : 'bg-gray-100 text-gray-900';
  return (
    <span
      className={`grid h-[22px] min-w-[26px] place-items-center rounded-sm px-1 text-xs font-semibold tabular-nums ${cls}`}
    >
      {cell.total}
    </span>
  );
}

/** Background tint per column, so the three bands stay distinct while scrolling. */
export function zoneClass(t: Track): string {
  const band = trackBand(t);
  if (band === 'past') return 'bg-gray-50';
  if (band === 'lookahead') return 'bg-amber-50';
  return '';
}

export function OwnerTag({ owner }: { owner: string | null }) {
  if (!owner) {
    return (
      <span
        className="shrink-0 text-[11px] leading-4 text-rose-600"
        title="No lead in the org chart — TA needs an owner before the round starts"
      >
        owner not set
      </span>
    );
  }
  return <span className="shrink-0 text-[11px] leading-4 text-gray-400">{owner}</span>;
}

const shortName = (email: string) => {
  const local = email.split('@')[0] || email;
  const first = local.split(/[._-]/)[0];
  return first ? first[0].toUpperCase() + first.slice(1) : email;
};

export function relativeTime(iso: string | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

export function EditedTag({ by, at }: { by?: string; at?: string }) {
  if (!by) return null;
  return (
    <span className="shrink-0 text-[11px] leading-4 text-gray-400">
      {shortName(by)} · {relativeTime(at)}
    </span>
  );
}

export const Caret = ({ open }: { open: boolean }) => (
  <span
    className={`grid h-4 w-4 shrink-0 place-items-center text-gray-400 transition-transform ${
      open ? 'rotate-90' : ''
    }`}
    aria-hidden
  >
    <svg viewBox="0 0 8 8" className="h-2 w-2">
      <path d="M2 0 L7 4 L2 8 Z" fill="currentColor" />
    </svg>
  </span>
);

/**
 * ⚠️ NATIVE `<input>` AND `<select>`, DELIBERATELY — the same documented
 * deviation the Role Prioritisation tab records for `prioritisationUi.tsx`, and
 * for the same three reasons. These are 22-pixel controls inside a 13-pixel data
 * grid with six fixed tracks per row:
 *
 *  1. With no `label`, the design-system `Input` renders BORDERLESS at 16px
 *     (`CoreInput` applies `py-0` and no border) and hides its placeholder until
 *     focus, so an empty cell reads as a blank strip with nothing to say it is a
 *     field. That is the exact failure already recorded for the reply-editor
 *     prompt ("i cant click on the AI box nor write something").
 *  2. A `className` passed to it REPLACES its layout and reset classes rather
 *     than merging, so an atom shrunk into a grid cell is not a smaller atom —
 *     it is a different control with the atom's behaviour removed.
 *  3. The `Select` trigger's intrinsic width wraps a value like "Core Functions"
 *     onto two lines in a fixed 92px track, which breaks the row alignment this
 *     whole file exists to hold.
 *
 * The rule stands everywhere else in this app. Reimplementing a labelled form
 * field would be wrong; this is not that.
 */
export const inputCls =
  'h-6 min-w-0 rounded-sm border border-transparent bg-transparent px-1.5 text-[13px] ' +
  'hover:border-gray-300 hover:bg-white focus:border-blue-500 focus:bg-white focus:outline-none';

export const selectCls =
  'h-6 shrink-0 cursor-pointer rounded-sm border border-transparent bg-transparent px-0.5 ' +
  'text-xs tabular-nums text-gray-600 hover:border-gray-300 hover:bg-white ' +
  'focus:border-blue-500 focus:bg-white focus:outline-none';

export const numCls =
  'h-6 shrink-0 rounded-sm border border-transparent bg-transparent text-center text-xs ' +
  'tabular-nums text-gray-600 hover:border-gray-300 hover:bg-white ' +
  'focus:border-blue-500 focus:bg-white focus:outline-none';

export const fmtFte = (v: number) => (Number(v).toFixed(2).replace(/\.?0+$/, '') || '0');

/**
 * Read a number out of an input, tolerating a COMMA decimal separator.
 *
 * `<input type="number">` normalises its own value in most browsers, but not
 * reliably across locales — on a German machine "0,8" can arrive verbatim, and
 * `Number('0,8')` is NaN. The old code fell back to 1 on NaN, so a lead who
 * typed 0,8 silently got a full-time role. Returns null when there is genuinely
 * no number, so the caller decides what "empty" means rather than inheriting a
 * fallback.
 */
export function parseNum(raw: string): number | null {
  const cleaned = String(raw ?? '').trim().replace(',', '.');
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}
